import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AIEngineBadge from "@/components/AIEngineBadge";
import PermissionNotice from "@/components/PermissionNotice";
import {
  useAnalysisList,
  useCreateAnalysis,
  useDeleteAnalysis,
} from "@/hooks/useAnalysis";
import { useAuth } from "@/hooks/useAuth";
import type { AnalysisCreate, AnalysisType } from "@/types";
import { apiErrorDetail } from "@/utils/apiError";
import {
  analysisStatusColor,
  analysisStatusLabels,
  analysisTypeLabels,
} from "@/utils/labels";

const INITIAL_FORM: AnalysisCreate = {
  sample_id: 0,
  analysis_type: "full_analysis",
};

export default function AnalysisPage() {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const { data: analyses, isLoading, error } = useAnalysisList();
  const createAnalysis = useCreateAnalysis();
  const deleteAnalysis = useDeleteAnalysis();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<AnalysisCreate>(INITIAL_FORM);

  const handleCreate = async () => {
    if (!form.sample_id) {
      alert("시료 ID는 필수 입력 항목입니다.");
      return;
    }
    try {
      await createAnalysis.mutateAsync(form);
      setForm(INITIAL_FORM);
      setShowForm(false);
    } catch (err) {
      // 서버의 한국어 사유를 버리지 않는다 — 409(이미 분석이 진행 중입니다,
      // 분석 #N)는 사유를 봐야 진행 중인 분석을 찾아갈 수 있다 (SVD-212-09).
      alert(apiErrorDetail(err, "분석 생성에 실패했습니다."));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("정말 이 분석을 삭제하시겠습니까?")) return;
    try {
      await deleteAnalysis.mutateAsync(id);
    } catch {
      alert("삭제에 실패했습니다.");
    }
  };

  const formatDatetime = (dt: string | null) => {
    if (!dt) return "-";
    return new Date(dt).toLocaleString("ko-KR");
  };

  const formatProcessingTime = (ms: number | null) => {
    if (ms == null) return "-";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">분석 관리</h1>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-gray-200 rounded mb-3" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">분석 관리</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 font-medium">
            데이터를 불러오는 중 오류가 발생했습니다.
          </p>
          <p className="text-red-500 text-sm mt-1">
            {(error as Error).message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">분석 관리</h1>
        <p className="text-gray-600">분석을 생성하고 관리할 수 있습니다.</p>
      </div>

      {/* 새 분석 생성 */}
      {permissions?.canRunAnalysis ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">신규 분석</h2>
            {!showForm && (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                + 새 분석
              </button>
            )}
          </div>

          {showForm ? (
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      시료 ID *
                    </span>
                    <input
                      type="number"
                      value={form.sample_id || ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          sample_id: Number(e.target.value),
                        })
                      }
                      placeholder="분석할 시료의 ID"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </label>
                </div>
                <div>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      분석 유형
                    </span>
                    <select
                      value={form.analysis_type}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          analysis_type: e.target.value as AnalysisType,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="pattern_matching">패턴 매칭</option>
                      <option value="compound_inference">화합물 추론</option>
                      <option value="hazard_assessment">위험성 평가</option>
                      <option value="full_analysis">전체 분석</option>
                    </select>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setForm(INITIAL_FORM);
                  }}
                  disabled={createAnalysis.isPending}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={createAnalysis.isPending}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {createAnalysis.isPending ? "생성 중..." : "분석 생성"}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              새로운 분석을 생성하려면 "새 분석" 버튼을 클릭하세요.
            </div>
          )}
        </div>
      ) : (
        <PermissionNotice action="분석 생성" requiredRole="분석관" />
      )}

      {/* 분석 목록 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">분석 목록</h2>
          <p className="text-sm text-gray-500 mt-1">
            총 {analyses?.length ?? 0}개의 분석 · 행을 클릭하면 대시보드로
            이동합니다
          </p>
        </div>

        {analyses && analyses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    분석 유형
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    시료 ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    시작 시간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    완료 시간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    처리 시간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    AI 엔진
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    주요 화합물
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {analyses.map((analysis) => (
                  // biome-ignore lint/a11y/useSemanticElements: <tr>은 <a>로 변환 불가 — 행 단위 navigation 패턴은 role="link"+tabIndex로 구현
                  <tr
                    key={analysis.id}
                    onClick={() =>
                      navigate(`/analyses/${analysis.id}/dashboard`)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/analyses/${analysis.id}/dashboard`);
                      }
                    }}
                    tabIndex={0}
                    role="link"
                    aria-label={`분석 ${analysis.id} 대시보드 열기`}
                    className="group hover:bg-blue-50 cursor-pointer focus:bg-blue-50 focus:outline-none"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/analyses/${analysis.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        {analysis.id}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {analysisTypeLabels[analysis.analysis_type] ??
                        analysis.analysis_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${analysisStatusColor(analysis.status)}`}
                      >
                        {analysisStatusLabels[analysis.status] ??
                          analysis.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <Link
                        to={`/samples/${analysis.sample_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        {analysis.sample_id}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDatetime(analysis.started_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDatetime(analysis.completed_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatProcessingTime(analysis.processing_time_ms)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <AIEngineBadge
                        engine={
                          analysis.model_version?.includes("inha")
                            ? "inha_jsd"
                            : "mock"
                        }
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {analysis.results[0]?.compound_name ?? "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center justify-end space-x-3">
                        {permissions?.canDeleteAnalysis && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(analysis.id);
                            }}
                            disabled={deleteAnalysis.isPending}
                            className="text-red-600 hover:text-red-900 font-medium disabled:opacity-50 cursor-pointer"
                          >
                            삭제
                          </button>
                        )}
                        <span
                          aria-hidden="true"
                          className="text-blue-500 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity"
                        >
                          →
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <p className="text-lg font-medium text-gray-900 mb-2">
              등록된 분석이 없습니다
            </p>
            <p className="text-gray-500">
              위의 "새 분석" 버튼으로 분석을 추가하세요.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
